import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function LandlordApplicationPage() {
  const [formData, setFormData] = useState({
    ownerName: '',
    email: '',
    phone: '',
    propertyAddress: '',
    propertyType: '',
    bedrooms: '',
    bathrooms: '',
    monthlyRentExpectation: '',
    propertyCondition: '',
    currentlyRented: '',
    availableDate: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

      const response = await fetch(`${supabaseUrl}/functions/v1/send-form-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          formType: 'landlord-application',
          formData: {
            ownerName: formData.ownerName,
            email: formData.email,
            phone: formData.phone,
            propertyAddress: formData.propertyAddress,
            propertyType: formData.propertyType,
            bedrooms: formData.bedrooms,
            bathrooms: formData.bathrooms,
            monthlyRentExpectation: formData.monthlyRentExpectation,
            propertyCondition: formData.propertyCondition,
            currentlyRented: formData.currentlyRented,
            availableDate: formData.availableDate,
            additionalInfo: formData.additionalInfo,
          },
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          ownerName: '',
          email: '',
          phone: '',
          propertyAddress: '',
          propertyType: '',
          bedrooms: '',
          bathrooms: '',
          monthlyRentExpectation: '',
          propertyCondition: '',
          currentlyRented: '',
          availableDate: '',
          additionalInfo: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-teal-600 via-blue-600 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 slide-up">
            Partner With ARM
          </h1>
          <p className="text-xl md:text-2xl text-teal-50 max-w-3xl mx-auto slide-up">
            Join our network of property owners and start earning guaranteed monthly rent
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Partner With ARM?</h2>
            <p className="text-xl text-gray-600">Hassle-free property management with guaranteed returns</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-money-dollar-circle-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Guaranteed Monthly Rent</h3>
              <p className="text-gray-600">Receive consistent monthly payments regardless of occupancy. We handle all the risks and management.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Property Protection</h3>
              <p className="text-gray-600">Smart monitoring, regular inspections, and professional maintenance keep your property in excellent condition.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-6">
                <i className="ri-customer-service-2-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Zero Hassle Management</h3>
              <p className="text-gray-600">We handle guest screening, cleaning, maintenance, and all day-to-day operations. You just collect rent.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-teal-500 to-blue-600 p-8 text-white">
              <h2 className="text-3xl font-bold mb-2">Property Owner Application</h2>
              <p className="text-teal-50">Tell us about your property and let's start a profitable partnership</p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="ownerName" className="block text-sm font-semibold text-gray-700 mb-2">
                    Property Owner Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="ownerName"
                    name="ownerName"
                    required
                    value={formData.ownerName}
                    onChange={(e) => setFormData({ ...formData, ownerName: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300"
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="propertyType" className="block text-sm font-semibold text-gray-700 mb-2">
                    Property Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="propertyType"
                    name="propertyType"
                    required
                    value={formData.propertyType}
                    onChange={(e) => setFormData({ ...formData, propertyType: e.target.value })}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 cursor-pointer"
                  >
                    <option value="">Select property type</option>
                    <option value="Single Family House">Single Family House</option>
                    <option value="Townhouse">Townhouse</option>
                    <option value="Apartment/Condo">Apartment/Condo</option>
                    <option value="Duplex">Duplex</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="propertyAddress" className="block text-sm font-semibold text-gray-700 mb-2">
                  Property Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="propertyAddress"
                  name="propertyAddress"
                  required
                  value={formData.propertyAddress}
                  onChange={(e) => setFormData({ ...formData, propertyAddress: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300"
                  placeholder="123 Main St, City, NJ 07001"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label htmlFor="bedrooms" className="block text-sm font-semibold text-gray-700 mb-2">
                    Bedrooms <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="bedrooms"
                    name="bedrooms"
                    required
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 cursor-pointer"
                  >
                    <option value="">Select</option>
                    <option value="Studio">Studio</option>
                    <option value="1">1 Bedroom</option>
                    <option value="2">2 Bedrooms</option>
                    <option value="3">3 Bedrooms</option>
                    <option value="4">4 Bedrooms</option>
                    <option value="5+">5+ Bedrooms</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="bathrooms" className="block text-sm font-semibold text-gray-700 mb-2">
                    Bathrooms <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="bathrooms"
                    name="bathrooms"
                    required
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 cursor-pointer"
                  >
                    <option value="">Select</option>
                    <option value="1">1 Bathroom</option>
                    <option value="1.5">1.5 Bathrooms</option>
                    <option value="2">2 Bathrooms</option>
                    <option value="2.5">2.5 Bathrooms</option>
                    <option value="3">3 Bathrooms</option>
                    <option value="3+">3+ Bathrooms</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="monthlyRentExpectation" className="block text-sm font-semibold text-gray-700 mb-2">
                    Monthly Rent Expectation
                  </label>
                  <select
                    id="monthlyRentExpectation"
                    name="monthlyRentExpectation"
                    value={formData.monthlyRentExpectation}
                    onChange={(e) => setFormData({ ...formData, monthlyRentExpectation: e.target.value })}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 cursor-pointer"
                  >
                    <option value="">Select range</option>
                    <option value="$1,500 - $2,000">$1,500 - $2,000</option>
                    <option value="$2,000 - $2,500">$2,000 - $2,500</option>
                    <option value="$2,500 - $3,000">$2,500 - $3,000</option>
                    <option value="$3,000 - $4,000">$3,000 - $4,000</option>
                    <option value="$4,000+">$4,000+</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="propertyCondition" className="block text-sm font-semibold text-gray-700 mb-2">
                    Property Condition <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="propertyCondition"
                    name="propertyCondition"
                    required
                    value={formData.propertyCondition}
                    onChange={(e) => setFormData({ ...formData, propertyCondition: e.target.value })}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 cursor-pointer"
                  >
                    <option value="">Select condition</option>
                    <option value="Move-in Ready">Move-in Ready</option>
                    <option value="Needs Minor Updates">Needs Minor Updates</option>
                    <option value="Needs Major Renovation">Needs Major Renovation</option>
                    <option value="Recently Renovated">Recently Renovated</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="currentlyRented" className="block text-sm font-semibold text-gray-700 mb-2">
                    Currently Rented? <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="currentlyRented"
                    name="currentlyRented"
                    required
                    value={formData.currentlyRented}
                    onChange={(e) => setFormData({ ...formData, currentlyRented: e.target.value })}
                    className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 cursor-pointer"
                  >
                    <option value="">Select status</option>
                    <option value="Vacant - Available Now">Vacant - Available Now</option>
                    <option value="Occupied - Lease Ending Soon">Occupied - Lease Ending Soon</option>
                    <option value="Occupied - Long-term Lease">Occupied - Long-term Lease</option>
                    <option value="Self-Occupied">Self-Occupied</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="availableDate" className="block text-sm font-semibold text-gray-700 mb-2">
                  Available Date
                </label>
                <input
                  type="date"
                  id="availableDate"
                  name="availableDate"
                  value={formData.availableDate}
                  onChange={(e) => setFormData({ ...formData, availableDate: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300"
                />
              </div>

              <div>
                <label htmlFor="additionalInfo" className="block text-sm font-semibold text-gray-700 mb-2">
                  Additional Information
                </label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  rows={4}
                  maxLength={500}
                  value={formData.additionalInfo}
                  onChange={(e) => setFormData({ ...formData, additionalInfo: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-300 resize-none"
                  placeholder="Tell us about any special features, recent improvements, or specific requirements..."
                ></textarea>
                <p className="text-sm text-gray-500 mt-2">{formData.additionalInfo.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-teal-500 to-teal-600 text-white font-semibold rounded-lg hover:from-teal-600 hover:to-teal-700 transition-all duration-300 transform hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting Application...' : 'Submit Application'}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-green-800 font-medium">Thank you! Your application has been submitted successfully. Our team will review your property and contact you within 48 hours to discuss next steps.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-800 font-medium">Sorry, there was an error submitting your application. Please try again or contact us directly at <a href="tel:732-213-2063" className="underline">732-213-2063</a>.</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
